import { api } from '@goaltree/backend/convex/_generated/api'
import { captionSentence, coachVoiceColor } from '@goaltree/backend/convex/lib/coach'
import {
  FOCUS_DEFAULT_MINUTES,
  FOCUS_PRESETS,
  type FocusPlan,
  capFocusMinutes,
  focusCallMinutes,
} from '@goaltree/backend/convex/lib/voiceSession'
import { cn } from '@goaltree/ui/lib/utils'
import { useQuery } from 'convex/react'
import {
  Camera,
  Check,
  ChevronDown,
  ListChecks,
  MessageSquareText,
  Mic,
  MicOff,
  Pause,
  Phone,
  Play,
  Square,
  Timer,
  Video,
  VideoOff,
  Zap,
} from 'lucide-react'
import { Fragment, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { SelfCameraPreview } from '@/components/checkin-frame-ask'
import { CoachAvatar, useCoachFace } from '@/components/coach-avatar'
import { CoachScenePicker } from '@/components/coach-scene-picker'
import { SpokenText } from '@/components/spoken-text'
import { localDateKey } from '@/hooks/use-coach-chat'
import { useCoachStyleLabel } from '@/hooks/use-coach-nav-meta'
import type { FocusView, useVoiceCall } from '@/hooks/use-voice-call'
import { getSelfCamera, setSelfCamera, subscribeSelfCamera } from '@/lib/self-camera'

export type CallActionKind =
  | 'today'
  | 'task'
  | 'habit'
  | 'goal'
  | 'done'
  | 'undone'
  | 'deadline'
  | 'move'
  | 'goalTask'
  | 'freeze'
  | 'unfreeze'
  | 'grove'
  | 'logged'
  | 'commitment'
  | 'photo'

// Something the coach changed during a call, shown as an undoable card and
// listed again in the post-call summary.
export type CallAction = {
  id: string
  kind: CallActionKind
  title: string
  context?: string
  undone?: boolean
  // Absent when the change can't be reversed from the call (moving tasks).
  undo?: () => Promise<unknown>
}

export type VoiceCall = ReturnType<typeof useVoiceCall>
export type TranscriptLine = {
  id: string
  role: 'user' | 'assistant'
  content: string
  // Changes the coach made, saved as their own (text-less) lines.
  notes?: { description: string; undone: boolean }[]
}

function CallDivider() {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground/70">
      <span className="h-px flex-1 bg-border" />
      {t('callView.callStarted')}
      <span className="h-px flex-1 bg-border" />
    </div>
  )
}

function CallTranscript({
  lines,
  start,
  live,
  voice,
  name,
}: {
  lines: TranscriptLine[]
  // Index of the first line from this call; a divider marks it.
  start: number
  // The reply still being spoken; it's saved as a line once it finishes.
  live: string | null
  voice: string | undefined
  name: string
}) {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const first = useRef(true)
  useEffect(() => {
    ref.current?.scrollTo({
      top: ref.current.scrollHeight,
      behavior: first.current ? 'instant' : 'smooth',
    })
    first.current = false
  }, [lines.length, live])
  const coach = (text: string, key: string) => (
    <div key={key} className="flex gap-2">
      <CoachAvatar voice={voice} size={20} className="mt-0.5 shrink-0" />
      <p className="min-w-0 flex-1 text-[13px] leading-snug">{text}</p>
    </div>
  )
  return (
    <div
      ref={ref}
      className="relative mx-3 mb-2 flex min-h-0 flex-[1.4] flex-col gap-3 overflow-y-auto rounded-2xl border border-border/60 bg-card/70 p-3 backdrop-blur-md"
    >
      {lines.map((l, i) => (
        <Fragment key={l.id}>
          {i === start && <CallDivider />}
          {l.role === 'user' ? (
            <div className="flex justify-end">
              <p className="max-w-[85%] rounded-2xl rounded-tr-sm bg-foreground px-3 py-1.5 text-[13px] leading-snug text-background">
                {l.content}
              </p>
            </div>
          ) : l.content.trim() ? (
            coach(l.content, `${l.id}-c`)
          ) : l.notes?.length ? (
            <div className="flex flex-wrap gap-1.5 pl-7">
              {l.notes.map((n) => (
                <span
                  key={n.description}
                  className={cn(
                    'flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10.5px] font-medium text-primary-text',
                    n.undone && 'opacity-50 line-through',
                  )}
                >
                  <Check className="h-3 w-3" strokeWidth={2.5} />
                  {n.description}
                </span>
              ))}
            </div>
          ) : null}
        </Fragment>
      ))}
      {start >= lines.length && <CallDivider />}
      {lines.length <= start && !live && (
        <p className="py-2 text-center text-xs text-muted-foreground">
          {t('callView.transcriptEmpty', { name })}
        </p>
      )}
      {live && coach(live, 'live')}
    </div>
  )
}

const CAPTIONS_KEY = 'coachCall.captions'
const pill =
  'flex cursor-pointer items-center gap-1.5 rounded-full border border-border/70 bg-card/70 px-3 py-1.5 text-xs font-semibold text-foreground backdrop-blur-md transition-colors hover:bg-muted'
const LOW_SECONDS = 120
const TOAST_MS = 6000
const MAX_TOASTS = 3

export function fmtCallTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export function reducedMotion() {
  return (
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export function useCaptionsPref() {
  const [on, setOn] = useState(() => {
    try {
      return localStorage.getItem(CAPTIONS_KEY) !== '0'
    } catch {
      return true
    }
  })
  function toggle() {
    setOn((v) => {
      try {
        localStorage.setItem(CAPTIONS_KEY, v ? '0' : '1')
      } catch {
        // storage unavailable — keep it for this session
      }
      return !v
    })
  }
  return [on, toggle] as const
}

// The live face's <video> belongs to the call session, so it moves between the
// full view and the PiP card instead of being recreated (Simli is bound to it).
export function FaceVideo({
  video,
  className,
  style,
}: {
  video: HTMLVideoElement | null
  className?: string
  style?: React.CSSProperties
}) {
  const slot = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const el = slot.current
    if (!el || !video) return
    video.className = 'absolute inset-0 size-full object-cover'
    el.appendChild(video)
    void video.play().catch(() => {})
    return () => {
      if (video.parentNode === el) el.removeChild(video)
    }
  }, [video])
  return <div ref={slot} className={className} style={style} />
}

function ActionText({ action }: { action: CallAction }) {
  return (
    <Trans
      i18nKey={`callView.added.${action.kind}`}
      values={{ title: action.title }}
      components={{ b: <b className="font-bold" /> }}
    />
  )
}

function CtrlButton({
  label,
  on,
  onClick,
  aria,
  children,
}: {
  label: string
  on?: boolean
  onClick: () => void
  aria: string
  children: React.ReactNode
}) {
  return (
    <div className="flex min-w-0 flex-col items-center gap-1.5 text-[10px] text-muted-foreground">
      <button
        type="button"
        onClick={onClick}
        aria-label={aria}
        title={aria}
        aria-pressed={on}
        className={cn(
          'flex size-12 shrink-0 cursor-pointer items-center justify-center rounded-full border transition-colors',
          on
            ? 'border-foreground bg-foreground text-background'
            : 'border-border bg-card text-foreground hover:bg-muted',
        )}
      >
        {children}
      </button>
      <span className="max-w-full truncate">{label}</span>
    </div>
  )
}

// Choose a Focus session's length and (optionally) the task or habit it's for.
// remainingSeconds: call minutes left, which cap the session.
export function FocusPicker({
  name,
  remainingSeconds,
  onStart,
  onCancel,
}: {
  name: string
  remainingSeconds: number | null
  onStart: (plan: FocusPlan) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [minutes, setMinutes] = useState(FOCUS_DEFAULT_MINUTES)
  const [taskId, setTaskId] = useState<string | null>(null)
  const today = useQuery(api.voiceCall.todayList, { localDate: localDateKey() })
  const options = today
    ? [
        ...today.mustDo,
        ...today.tasks,
        ...today.habits.filter((h) => !h.done).map((h) => ({ id: h.id, title: h.title })),
      ].slice(0, 8)
    : []
  const fit = remainingSeconds != null ? capFocusMinutes(minutes, remainingSeconds) : null
  const chip = (on: boolean) =>
    cn(
      'max-w-full cursor-pointer truncate rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
      on
        ? 'border-primary bg-primary/15 text-primary-text'
        : 'border-border bg-card text-foreground hover:bg-muted',
    )
  function start() {
    const task = options.find((o) => o.id === taskId)
    onStart({
      minutes: fit?.minutes ?? minutes,
      ...(task ? { task: task.title, taskId: task.id } : {}),
    })
  }
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-border bg-popover p-4 text-foreground shadow-2xl">
      <div className="flex items-center gap-2">
        <Timer className="h-4 w-4 text-primary" />
        <p className="text-[15px] font-bold">{t('callView.focus.title')}</p>
      </div>
      <p className="text-xs text-muted-foreground">{t('callView.focus.desc', { name })}</p>
      <div className="flex flex-wrap gap-1.5">
        {FOCUS_PRESETS.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMinutes(m)}
            aria-pressed={minutes === m}
            className={chip(minutes === m)}
          >
            {t('callView.focus.minutes', { count: m })}
          </button>
        ))}
      </div>
      {options.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground/70">
            {t('callView.focus.workOn')}
          </span>
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setTaskId(null)}
              aria-pressed={taskId === null}
              className={chip(taskId === null)}
            >
              {t('callView.focus.anything')}
            </button>
            {options.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setTaskId(o.id)}
                aria-pressed={taskId === o.id}
                className={chip(taskId === o.id)}
              >
                {o.title}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="text-[11px] text-muted-foreground">
        {remainingSeconds != null && (
          <p>{t('callView.focus.callTimeLeft', { count: Math.floor(remainingSeconds / 60) })}</p>
        )}
        <p>
          {t('callView.focus.estimate', {
            count: focusCallMinutes(fit?.minutes ?? minutes),
            name,
          })}
        </p>
        {fit?.capped && (
          <p className="text-amber-500">{t('callView.focus.capped', { count: fit.minutes })}</p>
        )}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex h-10 flex-1 cursor-pointer items-center justify-center rounded-xl border border-border text-[12.5px] font-semibold"
        >
          {t('callView.focus.cancel')}
        </button>
        <button
          type="button"
          onClick={start}
          disabled={remainingSeconds != null && remainingSeconds < 120}
          className="flex h-10 flex-1 cursor-pointer items-center justify-center rounded-xl bg-primary text-[13px] font-extrabold text-primary-foreground disabled:cursor-default disabled:opacity-50"
        >
          {t('callView.focus.start')}
        </button>
      </div>
    </div>
  )
}

const FOCUS_RING = 168

// The running Focus session: a countdown ring with the task, pause and end.
function FocusPanel({
  focus,
  name,
  color,
  unreachable,
  onPause,
  onEnd,
  onTalk,
}: {
  focus: FocusView
  name: string
  color: string
  unreachable: boolean
  onPause: () => void
  onEnd: () => void
  onTalk: () => void
}) {
  const { t } = useTranslation()
  const r = FOCUS_RING / 2 - 6
  const circ = 2 * Math.PI * r
  const frac = Math.max(0, Math.min(1, focus.leftSeconds / (focus.minutes * 60)))
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: FOCUS_RING, height: FOCUS_RING }}>
        <svg width={FOCUS_RING} height={FOCUS_RING} className="-rotate-90" aria-hidden>
          <circle
            cx={FOCUS_RING / 2}
            cy={FOCUS_RING / 2}
            r={r}
            fill="none"
            strokeWidth={6}
            className="stroke-border"
          />
          <circle
            cx={FOCUS_RING / 2}
            cy={FOCUS_RING / 2}
            r={r}
            fill="none"
            strokeWidth={6}
            strokeLinecap="round"
            stroke={color}
            strokeDasharray={circ}
            strokeDashoffset={circ * (1 - frac)}
            className="transition-[stroke-dashoffset] duration-1000 ease-linear"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
            {t(focus.paused ? 'callView.focus.paused' : 'callView.focus.label')}
          </span>
          <span
            role="timer"
            className={cn(
              'font-mono text-[34px] font-extrabold tabular-nums',
              focus.paused && 'opacity-60',
            )}
          >
            {fmtCallTime(focus.leftSeconds)}
          </span>
        </div>
      </div>
      <p className="max-w-[260px] truncate text-center text-sm font-bold">
        {focus.task ?? t('callView.focus.title')}
      </p>
      {focus.capped && (
        <p className="text-center text-[11px] text-amber-500">
          {t('callView.focus.capped', { count: focus.minutes })}
        </p>
      )}
      <p role="status" className="text-center text-[11.5px] text-muted-foreground">
        {unreachable
          ? t('callView.focus.unreachable', { name })
          : focus.line === 'open'
            ? t('callView.focus.listening', { name })
            : focus.line === 'waking'
              ? t('callView.focus.waking', { name })
              : t(focus.nextIsWrapUp ? 'callView.focus.nextWrapUp' : 'callView.focus.nextCheckIn', {
                  name,
                  time: fmtCallTime(focus.nextInSeconds),
                })}
      </p>
      {focus.line !== 'open' && (
        <button
          type="button"
          onClick={onTalk}
          disabled={focus.line === 'waking'}
          className="flex cursor-pointer items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-extrabold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-default disabled:opacity-60"
        >
          <Mic className="h-4 w-4" />
          {t('callView.focus.talk', { name })}
        </button>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onPause}
          className="flex cursor-pointer items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold hover:bg-muted"
        >
          {focus.paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
          {t(focus.paused ? 'callView.focus.resume' : 'callView.focus.pause')}
        </button>
        <button
          type="button"
          onClick={onEnd}
          className="flex cursor-pointer items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold hover:bg-muted"
        >
          <Square className="h-3 w-3" />
          {t('callView.focus.end')}
        </button>
      </div>
    </div>
  )
}

export function CoachCallView({
  call,
  voice,
  name,
  limitSeconds,
  canUpgrade,
  actions,
  onUndo,
  onUpgrade,
  translucent = false,
  transcript,
  transcriptStart = 0,
  onMinimize,
}: {
  call: VoiceCall
  voice: string | undefined
  name: string
  limitSeconds: number | null
  // Trial minutes can be topped up by upgrading; Pro's monthly cap can't.
  canUpgrade: boolean
  actions: CallAction[]
  onUndo: (id: string) => void
  onUpgrade: () => void
  // Fullscreen calls let the page show faintly through.
  translucent?: boolean
  // Lines said so far in this call; shows the Transcript toggle when given.
  transcript?: TranscriptLine[]
  transcriptStart?: number
  onMinimize?: () => void
}) {
  const { t } = useTranslation()
  const color = coachVoiceColor(voice)
  const realistic = useCoachFace(voice)
  const selfCam = useSyncExternalStore(subscribeSelfCamera, getSelfCamera, () => ({
    on: false,
    error: null as null,
  }))
  const toggleMine = () => {
    const next = !getSelfCamera().on
    void setSelfCamera(next).then((ok) => {
      if (next && !ok) toast.error(t('callView.myCameraDenied'))
    })
  }
  // Video off (or Simli refused/dropped) → the coach's illustrated counterpart in
  // the speaking ring, never a frozen full-screen portrait.
  const face = call.faceShown ? realistic : null
  const styleLabel = useCoachStyleLabel()
  const [captions, toggleCaptions] = useCaptionsPref()
  const [showTranscript, setShowTranscript] = useState(false)
  const [focusPicker, setFocusPicker] = useState(false)
  const focus = call.focus
  const compact = showTranscript && !!transcript
  const ring1 = useRef<HTMLSpanElement>(null)
  const ring2 = useRef<HTMLSpanElement>(null)
  const cameraOffset = useRef({ orbitX: 0, orbitY: 0, zoom: 1 })
  const cameraDrag = useRef<{
    pointerId: number
    x: number
    y: number
    orbitX: number
    orbitY: number
    moved: boolean
  } | null>(null)
  const connecting = call.status === 'connecting'
  const { phase } = call
  const thinking = phase === 'thinking'
  const levelsRef = useRef(call.levels)
  levelsRef.current = call.levels

  useEffect(() => {
    if (!call.faceLive) {
      cameraOffset.current = { orbitX: 0, orbitY: 0, zoom: 1 }
      cameraDrag.current = null
    }
  }, [call.faceLive])

  const resetCamera = () => {
    cameraOffset.current = { orbitX: 0, orbitY: 0, zoom: 1 }
    call.setFaceCamera(null, 0.55)
  }
  const closerCamera = () => {
    cameraOffset.current = { orbitX: 0, orbitY: 0, zoom: 1.22 }
    call.setFaceCamera({ zoom: 1.22 }, 0.55)
  }
  const startCameraDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!translucent || !realistic?.supportsTrinity2 || !call.faceLive || event.button !== 0) return
    cameraDrag.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      orbitX: cameraOffset.current.orbitX,
      orbitY: cameraOffset.current.orbitY,
      moved: false,
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }
  const moveCameraDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = cameraDrag.current
    if (!drag || drag.pointerId !== event.pointerId) return
    const dx = event.clientX - drag.x
    const dy = event.clientY - drag.y
    if (!drag.moved && Math.abs(dx) + Math.abs(dy) < 4) return
    drag.moved = true
    const orbitY = Math.max(-180, Math.min(180, drag.orbitY + dx * 0.55))
    const orbitX = Math.max(-65, Math.min(65, drag.orbitX - dy * 0.35))
    cameraOffset.current = { orbitX, orbitY, zoom: cameraOffset.current.zoom }
    call.setFaceCamera({
      orbit_x_off: orbitX,
      orbit_y_off: orbitY,
      zoom: cameraOffset.current.zoom,
    })
  }
  const stopCameraDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (cameraDrag.current?.pointerId === event.pointerId) cameraDrag.current = null
  }

  useEffect(() => {
    if (reducedMotion() || thinking) return
    let raf = 0
    let level = 0
    const frame = () => {
      const l = levelsRef.current()
      level = level * 0.7 + (phase === 'speaking' ? l.output : l.input) * 0.3
      if (ring1.current) {
        ring1.current.style.transform = `scale(${1 + level * 0.14})`
        ring1.current.style.opacity = String(0.45 + level * 0.5)
      }
      if (ring2.current) {
        ring2.current.style.transform = `scale(${1 + level * 0.2})`
        ring2.current.style.opacity = String(0.2 + level * 0.4)
      }
      raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)
    return () => cancelAnimationFrame(raf)
  }, [phase, thinking])

  // Each action gets its own few seconds on screen, so a burst (two habits
  // checked off back to back) shows every one instead of only the last.
  const seen = useRef(new Set<string>())
  const [toastIds, setToastIds] = useState<string[]>([])
  const toastTimers = useRef<ReturnType<typeof setTimeout>[]>([])
  useEffect(() => {
    const fresh = actions.filter((a) => !seen.current.has(a.id)).map((a) => a.id)
    if (!fresh.length) return
    for (const id of fresh) seen.current.add(id)
    setToastIds((ids) => [...ids, ...fresh])
    toastTimers.current.push(
      setTimeout(() => setToastIds((ids) => ids.filter((id) => !fresh.includes(id))), TOAST_MS),
    )
  }, [actions])
  useEffect(() => () => toastTimers.current.forEach(clearTimeout), [])
  const toasts = actions.filter((a) => toastIds.includes(a.id) && !a.undone).slice(-MAX_TOASTS)

  const remaining = call.remainingSeconds
  const low = remaining != null && remaining <= LOW_SECONDS
  const minLeft = remaining != null ? Math.ceil(remaining / 60) : null
  const meter =
    remaining != null && limitSeconds ? Math.max(0, Math.min(1, remaining / limitSeconds)) : 1

  const ringColor = phase === 'speaking' ? color : 'var(--primary)'
  const sentence = call.coachCaption
    ? captionSentence(call.coachCaption.text, call.coachCaption.chars)
    : null
  const youText = call.userCaption?.text
  const captionsBlock = (
    <div
      className={cn(
        'flex w-full flex-col gap-1.5 text-center',
        !face && 'min-h-[62px]',
        compact && 'hidden',
      )}
      aria-live="polite"
    >
      {call.muted ? (
        <p className="text-[11.5px] text-muted-foreground/70">
          {t('callView.mutedHint', { name })}
        </p>
      ) : !captions ? null : call.lastSpeaker === 'coach' && sentence?.text ? (
        <>
          {youText && (
            <p className="line-clamp-2 text-[11.5px] text-muted-foreground/70">
              {t('callView.you', { text: youText })}
            </p>
          )}
          <SpokenText
            text={sentence.text}
            chars={sentence.chars}
            highlight={color}
            className="text-sm font-semibold leading-snug"
          />
        </>
      ) : call.lastSpeaker === 'user' && youText ? (
        <p className="line-clamp-3 text-sm font-medium text-muted-foreground">
          “{youText}
          {call.userCaption?.final ? '' : '…'}”
        </p>
      ) : null}
    </div>
  )

  return (
    <div
      className={cn(
        'absolute inset-0 z-10 flex flex-col overflow-hidden transition-colors duration-500',
        face ? 'bg-black text-white' : 'text-foreground',
        !face && !translucent && 'bg-background',
      )}
    >
      <SelfCameraPreview className="absolute top-3 right-3 z-20 size-20" />
      {/* Realistic coach (as vaboo): the portrait fills the call, and Simli's
          live lip-synced video fades in over it once connected. */}
      {/* Kept mounted for realistic coaches so video can start mid-call. */}
      {realistic && (
        <div
          aria-hidden
          className={cn(
            'absolute inset-0 z-0 transition-opacity duration-500',
            translucent && realistic?.supportsTrinity2 && call.faceLive
              ? 'pointer-events-auto touch-none cursor-grab active:cursor-grabbing'
              : 'pointer-events-none',
            !face && 'opacity-0',
          )}
          style={{
            touchAction:
              translucent && realistic?.supportsTrinity2 && call.faceLive ? 'none' : undefined,
          }}
          onPointerDown={startCameraDrag}
          onPointerMove={moveCameraDrag}
          onPointerUp={stopCameraDrag}
          onPointerCancel={stopCameraDrag}
        >
          <img
            src={realistic.image}
            alt=""
            className={cn(
              'absolute inset-0 size-full object-cover transition-opacity duration-500',
              call.faceLive && 'opacity-0',
            )}
          />
          <FaceVideo
            video={call.faceVideoRef.current}
            className={cn(
              'absolute inset-0 transition-opacity duration-500',
              call.faceLive ? 'opacity-100' : 'opacity-0',
            )}
          />
          <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0.55),transparent_22%,transparent_55%,rgba(0,0,0,0.75))]" />
        </div>
      )}
      {face && (
        // Name + state live up here so nothing sits on the coach's face.
        <span className="pointer-events-none absolute top-20 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-black/45 px-2.5 py-1 text-[12px] font-bold text-white backdrop-blur-md">
          <span
            aria-label={t(
              thinking || connecting
                ? 'callView.thinking'
                : phase === 'speaking'
                  ? 'callView.speaking'
                  : 'callView.listening',
            )}
            className={cn(
              'size-[7px] rounded-full',
              thinking || connecting || call.faceStatus === 'connecting'
                ? 'bg-white/50 motion-safe:animate-pulse'
                : phase === 'speaking'
                  ? ''
                  : 'bg-[#3ac28e] motion-safe:animate-pulse',
            )}
            style={phase === 'speaking' && !thinking ? { background: color } : undefined}
          />
          {call.faceStatus === 'connecting' ? (
            t('callView.connectingFace', { name })
          ) : (
            <>
              {name}
              <span className="font-semibold text-white/60">
                · {styleLabel} · {t('callView.aiCoach')}
              </span>
            </>
          )}
        </span>
      )}
      {translucent && realistic?.supportsTrinity2 && call.faceLive && (
        <div className="absolute right-3 top-14 z-30">
          <CoachScenePicker
            call={{
              setFaceCamera: call.setFaceCamera,
              setFaceBackground: call.setFaceBackground,
              resetCamera,
              closerCamera,
            }}
          />
        </div>
      )}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 transition-opacity duration-500"
        style={{
          background: `radial-gradient(60% 45% at 50% 40%, ${color}29, transparent 70%)`,
          opacity: thinking || call.muted ? 0.4 : 1,
        }}
      />

      <div className="relative flex items-center justify-between px-4 pt-3.5 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-2">
          {onMinimize && (
            <button
              type="button"
              onClick={onMinimize}
              aria-label={t('callView.pip.minimize')}
              title={t('callView.pip.minimize')}
              className={cn(
                '-ml-1.5 flex size-7 cursor-pointer items-center justify-center rounded-full transition-colors',
                face
                  ? 'bg-black/40 text-white hover:bg-black/60'
                  : 'bg-muted/60 text-foreground hover:bg-muted',
              )}
            >
              <ChevronDown className="size-4" strokeWidth={2.4} />
            </button>
          )}
          {connecting ? (
            <span>{t('callView.connecting')}</span>
          ) : (
            <span
              className={cn(
                'flex items-center gap-1.5 font-mono tabular-nums',
                face ? 'text-white' : 'text-foreground',
              )}
            >
              <span className="h-[7px] w-[7px] rounded-full bg-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.2)]" />
              {fmtCallTime(call.elapsedSeconds)}
            </span>
          )}
        </span>
        <span className="flex items-center gap-2.5">
          {minLeft != null && (
            <span className={cn('flex items-center gap-1.5', low && 'text-amber-400')}>
              <span className="h-1 w-[46px] overflow-hidden rounded-sm bg-border">
                <span
                  className={cn('block h-full', low ? 'bg-amber-400' : 'bg-muted-foreground')}
                  style={{ width: `${meter * 100}%` }}
                />
              </span>
              {t('callView.minLeft', { count: minLeft })}
            </span>
          )}
        </span>
      </div>

      {(call.reconnecting || (low && minLeft != null)) && (
        <div className="absolute inset-x-3.5 top-[46px] z-10">
          {call.reconnecting ? (
            <div className="rounded-[10px] border border-border bg-card px-2.5 py-1.5 text-center text-[11px] text-muted-foreground">
              {t('callView.reconnecting')}
            </div>
          ) : (
            <button
              type="button"
              onClick={onUpgrade}
              disabled={!canUpgrade}
              className="w-full rounded-[10px] border border-amber-400/35 bg-amber-400/12 px-2.5 py-1.5 text-center text-[11px] text-amber-400 enabled:cursor-pointer"
            >
              {t(canUpgrade ? 'callView.lowMinutes' : 'callView.lowMinutesPro', {
                count: minLeft ?? 0,
              })}
            </button>
          )}
        </div>
      )}

      {toasts.length > 0 && (
        <div className="absolute inset-x-3.5 top-[84px] z-20 flex flex-col gap-1.5">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              role="status"
              className="flex items-center gap-2.5 rounded-xl border border-border bg-card px-2.5 py-2 text-[11.5px] shadow-[0_10px_30px_rgba(0,0,0,0.4)]"
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#7ed957]/20 text-[#7ed957]">
                <Check className="h-3 w-3" strokeWidth={3} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate">
                  <ActionText action={toast} />
                </p>
                {toast.context && (
                  <small className="block truncate text-[10px] text-muted-foreground">
                    {toast.context}
                  </small>
                )}
              </div>
              {toast.undo && (
                <button
                  type="button"
                  onClick={() => onUndo(toast.id)}
                  className="shrink-0 cursor-pointer font-bold text-primary"
                >
                  {t('common.undo')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {face ? (
        <div className="flex-1" />
      ) : focus && !compact ? (
        <div className="relative flex flex-1 flex-col items-center justify-center gap-3 px-5">
          <FocusPanel
            focus={focus}
            name={name}
            color={color}
            unreachable={call.focusUnreachable}
            onPause={call.toggleFocusPause}
            onEnd={call.endFocus}
            onTalk={call.talk}
          />
          {captionsBlock}
        </div>
      ) : (
        <div
          className={cn(
            'relative flex flex-col items-center justify-center gap-2.5 px-5',
            compact ? 'flex-none pt-6' : 'flex-1',
          )}
        >
          <button
            type="button"
            onClick={call.interrupt}
            disabled={phase !== 'speaking'}
            aria-label={t('callView.interrupt', { name })}
            // Outer ring sits 22px out and swells ~20%, so reserve room for it.
            className={cn(
              'relative flex items-center justify-center rounded-full disabled:cursor-default enabled:cursor-pointer',
              compact ? 'my-4' : 'my-10',
            )}
            style={{ opacity: thinking ? 0.85 : 1 }}
          >
            {!face && (
              <>
                <span
                  ref={ring1}
                  className={cn(
                    'pointer-events-none absolute -inset-2.5 rounded-full border-[3px] transition-[border-color] duration-300',
                    thinking && 'border-border',
                  )}
                  style={thinking ? { opacity: 1 } : { borderColor: ringColor, opacity: 0.65 }}
                />
                {!thinking && (
                  <span
                    ref={ring2}
                    className="pointer-events-none absolute -inset-[22px] rounded-full border-2 transition-[border-color] duration-300"
                    style={{ borderColor: ringColor, opacity: 0.3 }}
                  />
                )}
              </>
            )}
            {/* Video off shows the coach's own photo (CoachAvatar picks it), not an illustrated stand-in. */}
            <CoachAvatar
              voice={voice}
              size={compact ? 56 : 132}
              speaking={phase === 'speaking'}
              className={cn(
                realistic && 'motion-safe:animate-in motion-safe:fade-in motion-safe:duration-500',
              )}
            />
          </button>
          {!compact && <p className="mt-2 text-xl font-extrabold">{name}</p>}
          <p
            className={cn(
              'flex items-center gap-1.5 text-[13px] font-bold',
              phase === 'listening' && 'text-primary',
              (thinking || connecting) && 'text-muted-foreground',
            )}
            style={phase === 'speaking' ? { color } : undefined}
          >
            {connecting ? (
              t('callView.connecting')
            ) : thinking ? (
              <>
                <span className="flex gap-1" aria-hidden="true">
                  {[0, 150, 300].map((d) => (
                    <i
                      key={d}
                      className="block h-1.5 w-1.5 rounded-full bg-muted-foreground motion-safe:animate-pulse"
                      style={{ animationDelay: `${d}ms` }}
                    />
                  ))}
                </span>
                {t('callView.thinking')}
              </>
            ) : (
              `● ${t(phase === 'speaking' ? 'callView.speaking' : 'callView.listening')}`
            )}
          </p>

          {captionsBlock}
          <p
            className={cn(
              'text-[10.5px] text-muted-foreground/70',
              (phase !== 'speaking' || compact) && 'invisible',
              compact && 'hidden',
            )}
          >
            {t('callView.interruptHint', { name })}
          </p>
        </div>
      )}
      {face && focus && !compact && (
        <div className="relative flex justify-center px-5 pb-3">
          <div
            className="rounded-2xl bg-black/50 px-4 py-3 text-white backdrop-blur-md"
            style={{ '--muted-foreground': 'oklch(0.86 0 0)' } as React.CSSProperties}
          >
            <FocusPanel
              focus={focus}
              name={name}
              color={color}
              unreachable={call.focusUnreachable}
              onPause={call.toggleFocusPause}
              onEnd={call.endFocus}
              onTalk={call.talk}
            />
          </div>
        </div>
      )}
      {face && !compact && (
        <div className="relative px-5 pb-3">
          {(call.muted || (captions && (sentence?.text || youText))) && (
            <div
              className="px-3.5 py-2.5 text-white [text-shadow:0_1px_3px_rgb(0_0_0_/_0.9),0_0_10px_rgb(0_0_0_/_0.75)]"
              style={
                {
                  '--foreground': 'oklch(0.98 0 0)',
                  '--muted-foreground': 'oklch(0.86 0 0)',
                } as React.CSSProperties
              }
            >
              {captionsBlock}
            </div>
          )}
        </div>
      )}

      {compact && transcript && (
        <CallTranscript
          lines={transcript}
          start={transcriptStart}
          live={phase === 'speaking' ? (call.coachCaption?.text ?? null) : null}
          voice={voice}
          name={name}
        />
      )}

      {focusPicker && call.status === 'live' && !focus && (
        <div className="absolute inset-x-3 bottom-28 z-30">
          <FocusPicker
            name={name}
            remainingSeconds={call.remainingSeconds}
            onStart={(plan) => {
              setFocusPicker(false)
              call.startFocus(plan)
            }}
            onCancel={() => setFocusPicker(false)}
          />
        </div>
      )}

      {(transcript || (call.status === 'live' && !focus)) && (
        <div className="relative mb-3 flex justify-center gap-2">
          {transcript && (
            <button
              type="button"
              onClick={() => setShowTranscript((v) => !v)}
              aria-expanded={showTranscript}
              className={pill}
            >
              {showTranscript ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <MessageSquareText className="h-3.5 w-3.5" />
              )}
              {t(showTranscript ? 'callView.hideTranscript' : 'callView.transcript')}
            </button>
          )}
          {call.status === 'live' && !focus && (
            <button
              type="button"
              onClick={() => setFocusPicker((v) => !v)}
              aria-expanded={focusPicker}
              className={pill}
            >
              <Timer className="h-3.5 w-3.5" />
              {t('callView.focus.button')}
            </button>
          )}
          {call.status === 'live' && (
            <button type="button" onClick={toggleMine} aria-pressed={selfCam.on} className={pill}>
              <Camera className="h-3.5 w-3.5" />
              {t(selfCam.on ? 'callView.myCameraOn' : 'callView.myCamera')}
            </button>
          )}
        </div>
      )}

      {/* Equal columns so five live controls fit a phone-width sheet; End stays in the middle. */}
      <div className="relative mx-auto grid w-full max-w-sm auto-cols-[minmax(0,1fr)] grid-flow-col items-start justify-items-center gap-1 px-3 pb-7 pt-1.5">
        <CtrlButton
          label={call.muted ? t('callView.muted') : t('callView.mute')}
          on={call.muted}
          onClick={call.toggleMute}
          aria={call.muted ? t('coach.unmute') : t('coach.mute')}
        >
          {call.muted ? (
            <MicOff className="h-[18px] w-[18px]" />
          ) : (
            <Mic className="h-[18px] w-[18px]" />
          )}
        </CtrlButton>
        {call.status === 'live' && (
          <CtrlButton
            label={t('callView.captions')}
            on={captions}
            onClick={toggleCaptions}
            aria={t('callView.captions')}
          >
            <span className="text-xs font-extrabold">CC</span>
          </CtrlButton>
        )}
        <div className="flex min-w-0 flex-col items-center gap-1.5 text-[10px] text-muted-foreground">
          <button
            type="button"
            onClick={() => call.end()}
            aria-label={t('coach.callEnd')}
            className="flex size-14 shrink-0 cursor-pointer items-center justify-center rounded-full bg-red-500 text-white transition-transform hover:scale-105"
          >
            <Phone className="h-6 w-6 rotate-[135deg]" fill="currentColor" />
          </button>
          <span className="max-w-full truncate">{t('callView.end')}</span>
        </div>
        {call.status !== 'live' && (
          <CtrlButton
            label={t('callView.captions')}
            on={captions}
            onClick={toggleCaptions}
            aria={t('callView.captions')}
          >
            <span className="text-xs font-extrabold">CC</span>
          </CtrlButton>
        )}
        {call.status === 'live' && (
          <CtrlButton
            label={t('callView.quick')}
            on={call.quickOn}
            onClick={call.toggleQuick}
            aria={t(call.quickOn ? 'callView.quickOff' : 'callView.quickOn')}
          >
            <Zap className="h-[18px] w-[18px]" />
          </CtrlButton>
        )}
        {realistic && call.status === 'live' && (
          <CtrlButton
            label={t(call.videoOn ? 'callView.videoIsOn' : 'callView.videoIsOff')}
            on={call.videoOn}
            onClick={() => void call.toggleVideo()}
            aria={t(call.videoOn ? 'callView.videoOff' : 'callView.videoOn')}
          >
            {call.videoOn ? (
              <Video className="h-[18px] w-[18px]" />
            ) : (
              <VideoOff className="h-[18px] w-[18px]" />
            )}
          </CtrlButton>
        )}
      </div>
    </div>
  )
}

export function CoachCallSummary({
  voice,
  name,
  seconds,
  reason,
  keyPoints,
  actions,
  onToTasks,
  onBack,
  onDone,
}: {
  voice: string | undefined
  name: string
  seconds: number
  // The one-tap reason the call was started with, if any.
  reason?: string
  // undefined while loading; empty when the recap failed or had nothing to say.
  keyPoints: string[] | undefined
  actions: CallAction[]
  // Absent when the call was too short to have anything worth pulling out.
  onToTasks?: () => void
  onBack: () => void
  onDone: () => void
}) {
  const { t } = useTranslation()
  const changed = actions.filter((a) => !a.undone)
  return (
    <div className="absolute inset-0 z-10 flex flex-col gap-3 overflow-y-auto bg-background px-4 py-[18px] text-foreground">
      <div className="flex items-center gap-2.5">
        <CoachAvatar voice={voice} size={36} />
        <div>
          <p className="text-[15px] font-bold">{t('callView.callWith', { name })}</p>
          <p className="font-mono text-[11px] text-muted-foreground">
            {fmtCallTime(seconds)} · {t('callView.justNow')}
            {reason && ` · ${reason}`}
          </p>
        </div>
      </div>

      {keyPoints === undefined ? (
        <div className="flex flex-col gap-2 rounded-xl border border-border bg-card px-3 py-2.5">
          <span className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground/70">
            {t('callView.keyPoints')}
          </span>
          {[92, 78, 64].map((w) => (
            <span
              key={w}
              className="h-3 rounded bg-muted motion-safe:animate-pulse"
              style={{ width: `${w}%` }}
            />
          ))}
        </div>
      ) : (
        keyPoints.length > 0 && (
          <div className="flex flex-col gap-1.5 rounded-xl border border-border bg-card px-3 py-2.5 text-xs">
            <span className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground/70">
              {t('callView.keyPoints')}
            </span>
            <ul className="flex list-disc flex-col gap-1 pl-3.5">
              {keyPoints.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        )
      )}

      <div className="flex flex-col gap-1.5 rounded-xl border border-border bg-card px-3 py-2.5 text-xs">
        <span className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground/70">
          {t('callView.whatChanged')}
        </span>
        {changed.length === 0 ? (
          <p className="text-muted-foreground">{t('callView.nothingChanged')}</p>
        ) : (
          changed.map((a) => (
            <div key={a.id} className="flex items-center gap-2">
              <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#7ed957]/20 text-[#7ed957]">
                <Check className="h-2.5 w-2.5" strokeWidth={3} />
              </span>
              <div className="min-w-0">
                <p className="truncate">{a.title}</p>
                <small className="text-[10.5px] text-muted-foreground">
                  {t(`callView.changed.${a.kind}`)}
                </small>
              </div>
            </div>
          ))
        )}
      </div>

      {onToTasks && (
        <button
          type="button"
          onClick={onToTasks}
          className="flex h-9 shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-primary/40 bg-primary/10 text-[12.5px] font-semibold text-primary-text transition-colors hover:bg-primary/15"
        >
          <ListChecks className="h-3.5 w-3.5" />
          {t('callView.toTasks')}
        </button>
      )}

      <p className="text-[11px] text-muted-foreground">{t('callView.transcriptSaved')}</p>
      <button
        type="button"
        onClick={onBack}
        className="mt-auto flex h-[42px] shrink-0 cursor-pointer items-center justify-center rounded-[13px] bg-primary text-[13px] font-extrabold text-white dark:text-[#0a0a0a]"
      >
        {t('callView.backToChat')}
      </button>
      <button
        type="button"
        onClick={onDone}
        className="flex h-9 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-border text-[12.5px] font-semibold"
      >
        {t('callView.done')}
      </button>
    </div>
  )
}
